import { useState, useEffect } from 'react';
import { Form, Button as BsButton, OverlayTrigger, Popover } from "react-bootstrap";
import { InfoCircleFill } from "react-bootstrap-icons";
import { PageTitle } from "./Controls/PageTitle";
import { Select } from "./Controls/Select";
import { useForm } from "react-hook-form";
import { Input } from "./Controls/Input";
import { Button } from "./Controls/Button";
import { MediumPage } from "./Controls/MediumPage";
import { Navigate, useLoaderData, useNavigate, useLocation, useRouteLoaderData } from "react-router-dom";
import { Api } from "./Data/Api";
import { useParamIds } from "./Data/Common";
import { InviteParkerReq, Plan, Facility } from "./Data/ApiTypes";
import { showMoney, toCents } from "./Data/Money";
import { LocalDate } from '@js-joda/core';
import { DateTimeFormatter } from '@js-joda/core';
import { FieldError } from 'react-hook-form';
import { maskAtom } from "./Data/Atoms";
import { useAtom } from "jotai";
import { toast } from "react-toastify";
import { FEATURES, isFeatureEnabled } from "./Data/Features";

function getDayWithSuffix(day: number): string {
    if (day >= 11 && day <= 13) return `${day}th`;
    switch (day % 10) {
        case 1: return `${day}st`;
        case 2: return `${day}nd`;
        case 3: return `${day}rd`;
        default: return `${day}th`;
    }
}

type PaymentType = "immediate" | "deferred";

export function FacilityParkerInvitePage() {
    const nav = useNavigate();
    const location = useLocation();
    const plans = useLoaderData() as Plan[];
    const facility = useRouteLoaderData("facility") as Facility;
    const { facilityId } = useParamIds();
    const [msg, setMsg] = useState<string>();
    const [loading, setLoading] = useState(false);
    const [paymentType, setPaymentType] = useState<PaymentType>("immediate");
    const [enhancedGrouping, setEnhancedGrouping] = useState(false);
    const [, setMask] = useAtom(maskAtom);
    
    const isEnhancedGroupsEnabled = isFeatureEnabled(facility, FEATURES.ENHANCED_GROUPS);

    const prefillData = location.state as { email?: string; name?: string } | null;

    const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<InviteParkerReq>({
        defaultValues: {
            name: prefillData?.name || "",
            emailAddress: prefillData?.email || "",
            phoneNumber: "",
            message: "",
            quantity: 1,
            planId: plans?.[0]?.planId ?? 0,
            planPriceId: plans?.[0]?.prices?.[0]?.planPriceId ?? 0,
            startDate: LocalDate.now(),
            IsEnhancedGroup: false
        }
    });

    const selectedPlanId = watch("planId");
    const selectedPlanPriceId = watch("planPriceId");
    const prices = plans.find(p => p.planId === selectedPlanId)?.prices || [];
    const selectedPlan = plans.find(p => p.planId === selectedPlanId);
    const invoiceDay = selectedPlan?.dayOfMonthInvoice ?? 1;
    const selectedPriceEntry = prices.find(p => p.planPriceId === selectedPlanPriceId);
    const isUsageBasedBilling = selectedPlan?.isUsageBasedBilling ?? false;
    const isComplimentary = selectedPriceEntry !== undefined && toCents(selectedPriceEntry.price) === 0;

    // Reset price when plan changes
    useEffect(() => {
        if (prices.length > 0) {
            setValue("planPriceId", prices[0].planPriceId);
        }
    }, [selectedPlanId, prices, setValue]);

    // Complimentary plans do not use enhanced grouping or payment timing UI
    useEffect(() => {
        if (isComplimentary) {
            setEnhancedGrouping(false);
            setValue("IsEnhancedGroup", false);
        }
    }, [isComplimentary, setValue]);

    useEffect(() => {
        if (isUsageBasedBilling) {
            setValue("startDate", LocalDate.now());
        }
    }, [isUsageBasedBilling, setValue]);

    // Default to deferred payment when Enhanced Grouping is enabled
    useEffect(() => {
        if (enhancedGrouping) {
            setPaymentType("deferred");
        }
    }, [enhancedGrouping]);

    const vals = watch();

    if (plans.length === 0) {
        return <Navigate to={`/facility/${facilityId}`} />;
    }

    const finalizeSubmit = async (data : InviteParkerReq) => {
        setLoading(true);
        const plan = plans.find(p => p.planId === data.planId);
        const priceRow = plan?.prices.find(pp => pp.planPriceId === data.planPriceId);
        const complimentary = priceRow ? toCents(priceRow.price) === 0 : false;
        const res = await Api.facilityParkerInvite(facilityId, {
            ...data,
            requirePayment: complimentary ? false : (enhancedGrouping ? false : paymentType === "immediate"),
            IsEnhancedGroup: complimentary ? false : enhancedGrouping
        });
        if (res.isOk) {
            setMsg(typeof res.value === 'string' ? res.value : '');
           
            
            // Extract subscription ID and parker ID from response object
            let subscriptionId: number | null = null;
            let parkerId: number | null = null;
            
            if (typeof res.value === 'object' && res.value !== null) {
                subscriptionId = (res.value as any).subscriptionId ?? null;
                parkerId = (res.value as any).parkerId ?? null;
            }
            
            // If we have subscription info, redirect to subscription details page
            if (enhancedGrouping && subscriptionId && parkerId) {
                toast.success("Subscription saved and is ready for setup.");
                nav(`/facility/${facilityId}/parker/${parkerId}/plan/${subscriptionId}`, {
                    state: { fromInvite: true, from: "invite" }
                });
            } else {
                toast.success("Invitation sent successfully");
                nav(`/facility/${facilityId}/parker`);
            }
            return;            
        }

        setLoading(false);
        toast.error(`There was a problem with the invitation: ${res.error}.`);
        setMsg(res.error);
    }

    return (
        <MediumPage>
             <div className="d-flex align-items-center gap-3">
            <BsButton variant="link" className="p-0" onClick={() => nav(`/facility/${facilityId}/parker`)}>  ← Back</BsButton>
            <PageTitle>
                Invite
            
            </PageTitle>
        </div>
         
            <Form onSubmit={handleSubmit(finalizeSubmit)}>
                <Input
                    type="text"
                    label="Name"
                    {...register("name", { required: "Name is required" })}
                    error={errors.name}
                />

                <Input
                    type="email"
                    label="Email"
                    {...register("emailAddress", {
                        required: "Email is required",
                        pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address"
                        }
                    })}
                    error={errors.emailAddress}
                />

                <Input
                    type="tel"
                    label="Phone Number (Optional)"
                    {...register("phoneNumber")}
                    error={errors.phoneNumber}
                />

                <Select label="Plan"
                    {...register("planId", { valueAsNumber: true, required: "Plan is required" })}
                    error={errors.planId}>
                    {plans.map( plan => (
                        <option key={plan.planId} value={plan.planId}>
                            {plan.name}
                        </option>
                    ))}
                </Select>

                {!isUsageBasedBilling && (
                    <Select
                        label="Price"
                        {...register("planPriceId", {
                            required: "Price is required",
                            valueAsNumber: true } ) }
                        error={errors.planPriceId}>
                        {prices.map( price => (
                            <option key={price.planPriceId}
                                    value={price.planPriceId}>
                                {toCents(price.price) === 0 ? "Complimentary" : showMoney(price.price)}
                            </option>
                        ))}
                    </Select>
                )}
                
                {isEnhancedGroupsEnabled && !isComplimentary && (
                    <Form.Group className="mb-3">
                        <Form.Switch
                            id="enhancedGrouping"
                            label={
                                <span className="d-flex align-items-center gap-2">
                                    Enhanced Grouping
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Popover className="position-fixed" id="enhanced-grouping-popover">
                                                <Popover.Header as="h4">
                                                    Enhanced Grouping
                                                </Popover.Header>
                                                <Popover.Body>
                                                    Sub-groups and contracts can be created within the subscription before the invitation email is sent.
                                                </Popover.Body>
                                            </Popover>
                                        }
                                    >
                                        <InfoCircleFill 
                                            className="icon-shift-up"
                                            size={14} 
                                            style={{ cursor: 'help', marginLeft: '4px' }} 
                                        />
                                    </OverlayTrigger>
                                </span>
                            }
                            checked={enhancedGrouping}
                            onChange={(e) => {
                                setEnhancedGrouping(e.target.checked);
                                setValue("IsEnhancedGroup", e.target.checked);
                            }}
                        />
                        {enhancedGrouping && (
                            <Form.Text className="d-block text-muted ms-1 mt-2">
                                The account will receive an invoice after the subscription is configured. 
                            </Form.Text>
                        )}
                    </Form.Group>
                )}
                
                {!enhancedGrouping && !isComplimentary && (
                    <Form.Group className="mb-3">
                        <Form.Label className="mb-1">
                            Payment Type{' '}
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Popover className="position-fixed" id="payment-type-popover">
                                        <Popover.Header as="h4">
                                            Payment Type
                                        </Popover.Header>
                                        <Popover.Body>
                                            <div>
                                                Choose how the parker will be required to pay.
                                            </div>
                                            <div className="mt-2">
                                                <b>Require Payment Immediately</b> — The parker will be prompted to enter payment details at checkout.
                                            </div>
                                            <div className="mt-2">
                                                <b>Allow Deferred Invoicing/Payment</b> — The parker will skip payment for now and receive an invoice on the {getDayWithSuffix(invoiceDay)} of each month.
                                                <p className="mt-3" style={{fontSize:".8rem"}}><b>Note:</b> If invoice day has passed, the parker will receive a prorated invoice for the remainder of this month and another for the next.</p>
                                            </div>
                                        </Popover.Body>
                                    </Popover>
                                }
                            >
                                <InfoCircleFill 
                                    className="icon-shift-up"
                                    size={14} 
                                    style={{ cursor: 'help', marginLeft: '4px' }} 
                                />
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Select
                            value={paymentType}
                            onChange={(e) => setPaymentType(e.target.value as PaymentType)}>
                            <option value="immediate">Require Payment Immediately</option>
                            <option value="deferred">Allow Deferred Invoicing/Payment</option>
                        </Form.Select>
                    </Form.Group>
                )}

                {!isUsageBasedBilling && (
                    <Input
                        type="date"
                        label="Start Date"
                        {...register("startDate", { 
                            required: "Start date is required",
                            validate: {
                                validDate: (value: any) => {
                                    // Skip validation if empty - required will handle this
                                    if (!value || value === '') return true;
                                    
                                    // If it's already a LocalDate, it's valid
                                    if (value instanceof LocalDate) return true;
                                    
                                    // Must be a string in YYYY-MM-DD format
                                    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                                        return "Date must be in YYYY-MM-DD format";
                                    }

                                    try {
                                        const [year, month, day] = value.split('-').map(Number);
                                        const date = LocalDate.of(year, month, day);
                                        const firstOfMonth = LocalDate.now().withDayOfMonth(1);
                                        return date.isAfter(firstOfMonth.minusDays(1)) || "Start date must be on or after the 1st of this month";
                                    } catch (e) {
                                        return "Invalid date (e.g., February 30th is not valid)";
                                    }
                                }
                            },
                            setValueAs: (value: string | null) => {
                                if (!value) return value;
                                try {
                                    const [year, month, day] = value.split('-').map(Number);
                                    return LocalDate.of(year, month, day);
                                } catch (e) {
                                    return value; // Return original value if parsing fails
                                }
                            }
                        })}
                        min={LocalDate.now().withDayOfMonth(1).format(DateTimeFormatter.ISO_LOCAL_DATE).toString()}
                        error={errors.startDate as FieldError}
                    />
                )}
                
                {!isUsageBasedBilling && (
                    <Input
                        type="number"
                        label="Quantity"
                        min="1"
                        {...register("quantity", {
                            required: "Quantity is required",
                            valueAsNumber: true,
                            min: { value: 1, message: "Quantity must be at least 1" },
                        })}
                        error={errors.quantity} />
                )}
                <div className="d-flex justify-content-end gap-2 mb-4">
                    <Button type="button" className="w-50" loading={loading}  onClick={() => nav(-1)}>
                        Cancel                     
                    </Button>
                    <Button type="submit" className="w-50 btn-main" loading={loading}>
                        {enhancedGrouping ? "Continue Setup" : "Send Invite"}
                    </Button>
                </div>
            </Form>
        </MediumPage>
    );
}
